import { UUIDUtil } from '@aperza/util';
import clsx from 'clsx';
import React from 'react';
import { Form, InputGroup, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { renderDate } from '../display/AcTimestamp';
import { AcDropdown } from '../dropdown/AcDropdown';
import style from './AcFormField.module.scss';
import { AcFormFieldErrorMessage } from './AcFormFieldErrorMessage';
import { AcFormFieldLabel } from './AcFormFieldLabel';
import { AcFormFieldBadge } from './AcFormFieldBadge';
import { FaIcon } from '../util/FaIcon';
export const AcFormField = ({ fieldName, size, label, tooltip, type = 'text', // Default : 'text'
checkButton, inline, placeholder, showSelected, plaintext, readOnly, value, onChange, onBlur, onFocus, onEnter, onClick, invalid, rows, required, errorId, appendIcon, options = [], children, className, inputWidth, inputHeight, customHTML, disabled, hide, inputRef, forceHideRequiredLabel, hideLabel, labelClassName, testId, }) => {
    if (hide) {
        return null;
    }
    const onFormControlChange = (e) => {
        if (onChange) {
            onChange(fieldName, e.target.value);
        }
    };
    const onFormControlBlur = (e) => {
        if (onBlur) {
            onBlur(fieldName, e.target.value);
        }
    };
    const onFormControlFocus = (e) => {
        if (onFocus) {
            onFocus(fieldName, e.target.value);
        }
    };
    const onFormControlEnter = (e) => {
        if (onEnter) {
            if (e.keyCode === 13) {
                onEnter(fieldName, value);
            }
        }
    };
    const onChangeRadio = (e) => {
        if (!onChange) {
            return;
        }
        onChange(fieldName, e.target.value);
    };
    const onChangeCheck = (e) => {
        if (!onChange) {
            return;
        }
        const { checked, value: inputValue } = e.target;
        if (!Array.isArray(value))
            throw new Error('value is not array');
        let updatedValue = value.slice();
        if (checked) {
            if (!value.includes(inputValue)) {
                updatedValue.push(inputValue);
            }
        }
        else if (value.includes(inputValue)) {
            updatedValue = value.filter((v) => v !== inputValue);
        }
        onChange(fieldName, updatedValue);
    };
    const onChangeToggleButtonCheck = (checkValues) => {
        if (!Array.isArray(checkValues))
            throw new Error('checkValues is not array');
        if (onChange) {
            onChange(fieldName, checkValues);
        }
    };
    const onFormClick = (e) => {
        if (onClick) {
            onClick(fieldName, value);
        }
    };
    const showSelectionResults = () => {
        if (showSelected && Array.isArray(value) && value.length > 0) {
            const item = options?.find((e) => e.value === value[0]);
            if (item && item.label) {
                const selectionResult = item.label;
                if (value.length > 1) {
                    return (React.createElement("span", { className: "d-flex flex-row" },
                        React.createElement("span", { className: "text-truncate" }, selectionResult),
                        React.createElement("span", { className: "ml-auto" }, `+${value.length - 1}`)));
                }
                return React.createElement("span", { className: "text-truncate" }, selectionResult);
            }
        }
        return placeholder ?? '選択してください';
    };
    const renderField = () => {
        let valueD = value;
        const hasAppend = appendIcon || required;
        // Make default AcFormField with no props to be non component as it
        // does not work at all with no value and on change
        const formControl = (React.createElement(Form.Control, { size: size, ref: inputRef, style: {
                width: inputWidth ? `${inputWidth}px` : undefined,
                height: inputHeight ? `${inputHeight}px` : undefined,
            }, className: clsx(className, { [style.formControlHasAppend]: hasAppend }), onChange: onFormControlChange, onBlur: onFormControlBlur, disabled: disabled, onFocus: onFormControlFocus, placeholder: placeholder, plaintext: plaintext, readOnly: readOnly, value: (valueD ?? ''), rows: rows, onClick: onFormClick, as: type === 'textarea' ? 'textarea' : undefined, type: type === 'number' ? 'number' : undefined, "data-testid": testId || `acformfield-${fieldName ?? 'nofieldname'}-${type}` }));
        if (type === 'custom') {
            if (typeof customHTML === 'string' || React.isValidElement(customHTML)) {
                return customHTML;
            }
            if (typeof customHTML === 'function') {
                return customHTML(onChange, valueD, fieldName);
            }
            return undefined;
        }
        if (type === 'search') {
            const adornment = (React.createElement(FaIcon, { icon: "far fa-search", className: "fa-2x", style: {
                    position: 'absolute',
                    zIndex: 100,
                    pointerEvents: 'none',
                    paddingLeft: '1rem',
                    top: '0.9rem',
                } }));
            const clearButton = (React.createElement("i", { role: "button", className: "far fa-times text-secondary1 fa-fw fs-18", onClick: () => {
                    onEnter?.(fieldName, '');
                    onChange?.(fieldName, '');
                }, style: {
                    cursor: 'pointer',
                    position: 'absolute',
                    zIndex: 100,
                    right: '0rem',
                    padding: '1.3rem 2.4rem 1.2rem 1.2rem',
                } }));
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "d-flex align-items-stretch position-relative" },
                    adornment,
                    React.createElement(Form.Control, { size: size, onChange: onFormControlChange, onKeyUp: onFormControlEnter, placeholder: placeholder, className: `pl-9 no-x ${style.no_x}`, value: value }),
                    clearButton)));
        }
        if (type === 'date') {
            valueD = renderDate(value);
        }
        if (plaintext) {
            return (React.createElement(Form.Text, { className: `py-2 fs-default font-weight-bold border-bottom border-secondary6 w-100 ${style.break_word}` }, valueD ?? '-'));
        }
        if (type === 'text' && (appendIcon || (required && !forceHideRequiredLabel))) {
            return (
            /// TODO : Migrate out this component to AcFormControl
            React.createElement("div", { className: clsx({ 'position-relative': hasAppend }) },
                formControl,
                hasAppend && (React.createElement("div", { className: style.appendDiv },
                    appendIcon && React.createElement("span", { onClick: onFormClick }, appendIcon),
                    required && (React.createElement("span", { className: "mr-3" },
                        React.createElement(AcFormFieldBadge, { type: "required", size: size })))))));
        }
        if (type === 'checkbox' || type === 'radio') {
            if (checkButton) {
                return (React.createElement(ToggleButtonGroup, { type: "checkbox", value: value, name: fieldName, onChange: onChangeToggleButtonCheck }, options.map((option, index) => (React.createElement(ToggleButton, { key: `${option.label}${index}`, value: option.value, variant: 'outline-body', className: "btn-form-checkbox" }, option.label)))));
            }
            return (React.createElement(Form.Group, { key: inline ? `inline-${type}` : `custom-single-${type}` }, options.map((option, index) => (React.createElement(Form.Check, { className: "mb-2", id: `form-${type}-${option.value}-${label}-${UUIDUtil.uuid()}`, custom: true, disabled: option.disabled ? option.disabled : false, type: type, name: fieldName, inline: inline, label: option.label, key: `acformfield-${fieldName}-${type}-${option.value}`, value: option.value, checked: (Array.isArray(value) && value.includes(option.value)) || value === option.value, onChange: type === 'checkbox' ? onChangeCheck : onChangeRadio, "data-testid": `acformfield-${fieldName}-${type}-${option.value}` })))));
        }
        if (type === 'select') {
            return (React.createElement(InputGroup, null,
                React.createElement(Form.Control, { id: `form-${fieldName}-${type}`, custom: true, onChange: onFormControlChange, readOnly: readOnly, value: (valueD ?? ''), as: "select", disabled: disabled, "data-testid": testId },
                    placeholder && React.createElement("option", { value: "" }, placeholder),
                    options.map((option, index) => (React.createElement("option", { id: `form-${fieldName}-${type}-${option.value}`, disabled: option.disabled ? option.disabled : false, label: option.label, key: `${option.value}${index}`, value: option.value })))),
                React.createElement(InputGroup.Append, { className: style.inputgroup },
                    appendIcon && React.createElement(InputGroup.Text, { onClick: onFormClick }, appendIcon),
                    required && !readOnly && (React.createElement(InputGroup.Text, null,
                        React.createElement(AcFormFieldBadge, { type: "required", size: size }))))));
        }
        if (type === 'switch') {
            return (React.createElement(React.Fragment, null, options.map((option, index) => (React.createElement("div", { className: "d-flex flex-row align-items-center mt-1", key: index },
                React.createElement("div", { className: "d-flex", onClick: () => {
                        if (onChange && !readOnly && !disabled) {
                            onChange(fieldName, value === option.value ? option.offValue : option.value);
                        }
                    } },
                    option.offLabel && (React.createElement("span", { className: "pr-1", style: { cursor: 'pointer' } }, option.offLabel)),
                    React.createElement(Form.Check, { type: "switch", custom: true, disabled: disabled, label: option.label, readOnly // since state is managed by parent
                        : true, checked: value === option.value })))))));
        }
        if (type === 'selectCheck') {
            const dropdownItems = options.map((option, index) => {
                // return {
                //   key: `${option.label}_${option.value}`,
                //   name: option.value,
                //   title: (
                // <Form.Check
                //   id={`form-${option.value}-${UUIDUtil.uuid()}`}
                //   custom
                //   disabled={option.disabled ? option.disabled : false}
                //   name={fieldName} // all radios in a group need to have the same name
                //   label={option.label}
                //   key={`${option.label}${index}`}
                //   value={option.value}
                //   checked={Array.isArray(value) && value.includes(option.value!)}
                //   // added "!" non-null assertion operator that is here exactly for your use case
                //   onChange={onChangeCheck}
                // />;
                //   ),
                // };
                return (React.createElement("div", { className: "dropdown-item" },
                    React.createElement(Form.Check, { id: `form-${option.value}-${UUIDUtil.uuid()}`, custom: true, disabled: option.disabled ? option.disabled : false, name: fieldName, label: option.label, key: `${option.label}${index}`, value: option.value, checked: Array.isArray(value) && value.includes(option.value), 
                        // added "!" non-null assertion operator that is here exactly for your use case
                        onChange: onChangeCheck })));
            });
            return (React.createElement("div", { className: "d-flex" },
                React.createElement(AcDropdown, { toggleClassName: "w-100", toggleVariant: "sub4", title: React.createElement("span", { className: "text-body font-weight-normal mr-2 flex-grow-1" }, showSelectionResults()) }, dropdownItems)));
        }
        return formControl;
    };
    return (
    // fix for IE to set width correctly
    React.createElement("div", { style: { flex: '1 1 100%' } },
        React.createElement(Form.Group, { className: clsx(className, {
                'form-validation-error': invalid && errorId,
            }) },
            !hideLabel && (React.createElement(AcFormFieldLabel, { label: label, tooltip: tooltip, className: labelClassName, readOnly: readOnly, required: required && !readOnly && !forceHideRequiredLabel, type: type })),
            renderField(),
            children,
            invalid && errorId && !readOnly && React.createElement(AcFormFieldErrorMessage, { messageId: errorId }))));
};
AcFormField.displayName = 'AcFormField';
